import React, { useMemo, useState } from "react";
import { merge } from "lodash-es";

import { copy } from "shared/assets";
import { AddressForm, Card, FormBuilder, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { IAddress, IAPIState, ICardTheme, IFormButtons } from "shared/types";
import { IVenue, IVenuePostData } from "Venues/types";
import {
  getFormFields,
  defaultValues as standardDefaultValues,
} from "./formData";

interface VenueFormProps {
  apiStatus?: IAPIState;
  hasError?: boolean;
  defaultValues?: Record<string, any>;
  buttons?: IFormButtons;
  handleSubmit: (venue: IVenuePostData) => void;
  updatePlaceholder?: (venue: IVenue) => void;
  showExtendedInfo?: boolean;
  hasContainer?: boolean;
}

export const VenueForm: React.FC<VenueFormProps> = ({
  apiStatus,
  hasError,
  defaultValues: dataDefaultValues,
  buttons,
  handleSubmit,
  updatePlaceholder,
  showExtendedInfo,
  hasContainer,
  children,
}) => {
  const defaultValues = useMemo(
    () => merge({}, standardDefaultValues, dataDefaultValues),
    [dataDefaultValues]
  );

  const [partialVenue, setPartialVenue] = useState<IVenue | null>();
  const [address, setAddress] = useState<IAddress | null>(
    defaultValues?.address || null
  );
  const [partialAddress, setPartialAddress] = useState<IAddress | null>(
    defaultValues?.address || null
  );
  const [isTouched, setIsTouched] = useState(false);

  const preventSubmit = useMemo(
    () => !address || !isTouched,
    [address, isTouched]
  );

  const onSubmit = (partialVenue: IVenuePostData) => {
    if (address) {
      const venue: IVenuePostData = { ...partialVenue, address };
      handleSubmit(venue);
    }
  };

  const handleBlur = (venue: IVenue) => {
    !isTouched && setIsTouched(true);
    setPartialVenue(venue);

    updatePlaceholder &&
      updatePlaceholder({
        ...venue,
        ...(partialAddress && { address: partialAddress }),
      });
  };

  const handleAddressBlur = (address: IAddress | null) => {
    !isTouched && setIsTouched(true);

    setPartialAddress(address);

    updatePlaceholder &&
      partialVenue &&
      updatePlaceholder({
        ...partialVenue,
        ...(address && { address }),
      });
  };

  const submitAddress = (address: IAddress | null) => {
    !isTouched && setIsTouched(true);
    setAddress(address);
  };

  const fields = useMemo(
    () => getFormFields({ showExtendedInfo }),
    [showExtendedInfo]
  );

  return (
    <FormBuilder
      fields={fields}
      defaultValues={defaultValues}
      buttons={buttons}
      onSubmit={onSubmit}
      onBlur={handleBlur}
      preventSubmit={preventSubmit}
      isSubmitting={apiStatus === IAPIState.pending}
      apiErrorMessage={hasError}
    >
      <Card theme={hasContainer ? ICardTheme.primary : ICardTheme.secondary}>
        <Spacer size="m">
          <Title
            title={copy.venueProperties.address.title}
            asComponent="h2"
            size="m"
          />
          <AddressForm
            submitAddress={submitAddress}
            blurAddress={handleAddressBlur}
            defaultValues={defaultValues?.address}
          />
        </Spacer>
      </Card>
      {children}
    </FormBuilder>
  );
};
